import React, { useMemo } from 'react'
import classNames from 'classnames'
import { FormattedDataPoint } from '../features/device/'
import PropTypes from 'prop-types'
import './ForecastCurrentConditions.less'
import { isSomething } from '../common/ambient/'

export default function ForecastCurrentConditions ({
  data,
  mode = 'current',
  dailyHigh,
  dailyLow,
  sunTimes,
  dailySummary,
  // Legacy props for backward compatibility
  currently
}) {
  // Support both new 'data' prop and legacy 'currently' prop
  const forecastData = data || currently

  // Map the forecast data based on mode
  const mappedData = useMemo(() => {
    if (!forecastData) return null

    if (mode === 'daily') {
      // For daily forecasts, use Max values for display
      return {
        temperature: forecastData.temperatureMax,
        apparentTemperature: forecastData.apparentTemperatureMax,
        icon: forecastData.icon,
        precipIntensity: forecastData.precipIntensity,
        precipProbability: forecastData.precipProbability,
        windSpeed: forecastData.windSpeed,
        windBearing: forecastData.windBearing,
        humidity: forecastData.humidity,
        pressure: forecastData.pressure,
        uvIndex: forecastData.uvIndex
      }
    } else {
      // For current conditions, use as-is
      return {
        temperature: forecastData.temperature,
        apparentTemperature: forecastData.apparentTemperature,
        icon: forecastData.icon,
        precipIntensity: forecastData.precipIntensity,
        precipProbability: forecastData.precipProbability,
        windSpeed: forecastData.windSpeed,
        windBearing: forecastData.windBearing,
        humidity: forecastData.humidity,
        pressure: forecastData.pressure,
        uvIndex: forecastData.uvIndex
      }
    }
  }, [forecastData, mode])

  // Calculate high/low based on mode
  const highLow = useMemo(() => {
    if (dailyHigh !== undefined && dailyLow !== undefined) {
      return { high: dailyHigh, low: dailyLow }
    }
    if (mode === 'daily' && forecastData) {
      return {
        high: forecastData.temperatureMax,
        low: forecastData.temperatureMin
      }
    }
    return null
  }, [dailyHigh, dailyLow, mode, forecastData])

  if (!mappedData) {
    return null
  }

  return (
    <div className='current-wrap'>
      <div className='current-conditions'>
        <div className='temperature-display'>
          <div className={classNames('weather-icon', mappedData.icon)} />
          {/* <div className='summary'>{mappedData.summary}</div> */}
        </div>
        <div className='conditions-info'>
          {mode === 'daily' && highLow ? (
            <div className='high-low-large'>
              <div className='high-temp'>
                <span className='arrow up'>↑</span>
                <FormattedDataPoint
                  type='tempf'
                  value={highLow.high}
                  valueTransform={Math.round}
                  after='°'
                />
              </div>
              <div className='low-temp'>
                <span className='arrow down'>↓</span>
                <FormattedDataPoint
                  type='tempf'
                  value={highLow.low}
                  valueTransform={Math.round}
                  after='°'
                />
              </div>
            </div>
          ) : (
            <>
              <FormattedDataPoint
                type='tempf'
                value={mappedData.temperature}
                valueTransform={Math.round}
                after='°'
              />
              {isSomething(mappedData.apparentTemperature) && (
                <div className='weather-description'>
                  <div className='feels-like'>
                    Feels like <FormattedDataPoint
                      type='tempf'
                      value={mappedData.apparentTemperature}
                      valueTransform={Math.round}
                      after='°'
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className='forecast-summary__details'>
        {highLow && mode !== 'daily' && (
          <div className='label high-low'>
            <span className='high'>
              <span className='arrow up'>↑</span>
              <FormattedDataPoint type='tempf' value={highLow.high} valueTransform={Math.round} after='°' />
            </span>
            <span className='low'>
              <span className='arrow down'>↓</span>
              <FormattedDataPoint type='tempf' value={highLow.low} valueTransform={Math.round} after='°' />
            </span>
          </div>
        )}
        {(mode === 'daily' ? mappedData.precipProbability !== undefined : mappedData.precipIntensity !== undefined) && (
          <div className='label precip'>
            <div className='label-title'>Precip:</div>
            {mode === 'daily' ? (
              <span className='fdp'>{Math.round(mappedData.precipProbability * 100)}%</span>
            ) : (
              <FormattedDataPoint value={mappedData.precipIntensity} type='hourlyrainin' />
            )}
          </div>
        )}
        {sunTimes && (
          <>
            <div className='label sunrise'>
              <div className='label-title'>Sunrise:</div>
              <span className='fdp'>{sunTimes.sunrise.replace(' ', '')}</span>
            </div>
          </>
        )}
        {mappedData.windSpeed !== undefined && (
          <div className='label wind'>
            <div className='label-title'>Wind:</div>
            <FormattedDataPoint type='windspeedmph' value={mappedData.windSpeed} valueTransform={Math.round} />
            <FormattedDataPoint type='winddir' value={mappedData.windBearing} />
          </div>
        )}
        {sunTimes && (
          <div className='label sunset'>
            <div className='label-title'>Sunset:</div>
            <span className='fdp'>{sunTimes.sunset.replace(' ', '')}</span>
          </div>
        )}
        {mappedData.humidity !== undefined && (
          <div className='label humidity'>
            <div className='label-title'>Humidity:</div>
            <FormattedDataPoint type='humidity' value={mappedData.humidity} valueTransform={v => Math.round(v * 100)} />
          </div>
        )}
        {mappedData.pressure !== undefined && (
          <div className='label pressure'>
            <div className='label-title'>Pressure:</div>
            <FormattedDataPoint type='baromrelin' value={mappedData.pressure} valueTransform={Math.round} />
          </div>
        )}
        {mappedData.uvIndex !== undefined && (
          <div className='label uv'>
            <div className='label-title'>UV Index:</div>
            <span className='fdp'>{mappedData.uvIndex}</span>
          </div>
        )}
      </div>
      {dailySummary && (
        <p>
          {dailySummary}
        </p>
      )}
    </div>
  )
}

ForecastCurrentConditions.propTypes = {
  data: PropTypes.object,
  mode: PropTypes.oneOf(['current', 'daily']),
  dailyHigh: PropTypes.number,
  dailyLow: PropTypes.number,
  sunTimes: PropTypes.shape({
    sunrise: PropTypes.string,
    sunset: PropTypes.string
  }),
  dailySummary: PropTypes.string,
  // Legacy prop
  currently: PropTypes.object
}

ForecastCurrentConditions.displayName = 'ForecastCurrentConditions'
